import { Controller } from "@hotwired/stimulus"

const LAUNCHER_SIZE = 62
const LAUNCHER_GAP = 28
const LAUNCHER_MIN_RIGHT = 18
// 質問一覧は4件まで全体を表示し、5件目以降があれば5件目を半分だけ見せてスクロールできることを示す
const VISIBLE_QUESTION_COUNT = 4

export default class extends Controller {
  static targets = [
    "launcher",
    "questionList",
    "overlay",
    "panel",
    "screen",
    "answerQuestion",
    "answerBlock",
    "bodyInput",
    "confirmBody",
    "submitButton",
    "error",
  ]

  connect() {
    this._onKeydown = (event) => {
      if (event.key === "Escape") this.close()
    }

    // PCは起動ボタンを .mypage-main の右端から28px右に置く（Figma実測）
    this._main = document.querySelector(".mypage-main")
    this._onResize = () => {
      this.positionLauncher()
      this.observeFooter()
      if (!this.panelTarget.hidden) this.fitQuestionList()
    }

    // パネル上のホイール操作は背面のページではなくメニュー内のスクロールに使う
    this._onWheel = (event) => this.scrollPanel(event)
    this.panelTarget.addEventListener("wheel", this._onWheel, { passive: false })
    if (this._main) {
      this._resizeObserver = new ResizeObserver(this._onResize)
      this._resizeObserver.observe(this._main)
    }
    window.addEventListener("resize", this._onResize)
    this.positionLauncher()

    this._footer = document.querySelector(".footer")
    this.observeFooter()
  }

  disconnect() {
    window.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("resize", this._onResize)
    this.panelTarget.removeEventListener("wheel", this._onWheel)
    this._resizeObserver?.disconnect()
    this._footerObserver?.disconnect()
    document.body.classList.remove("is-qa-navigation-open")
  }

  open() {
    this.panelTarget.hidden = false
    this.overlayTarget.classList.add("is-open")
    this.launcherTarget.classList.add("is-hidden")
    document.body.classList.add("is-qa-navigation-open")
    window.addEventListener("keydown", this._onKeydown)
    this.showScreen("menu")
    this.fitQuestionList()
  }

  close() {
    if (this.panelTarget.hidden || this.panelTarget.classList.contains("is-closing")) return

    window.removeEventListener("keydown", this._onKeydown)

    const finish = () => {
      this.panelTarget.classList.remove("is-closing")
      this.overlayTarget.classList.remove("is-closing")
      this.panelTarget.hidden = true
      this.overlayTarget.classList.remove("is-open")
      this.launcherTarget.classList.remove("is-hidden")
      document.body.classList.remove("is-qa-navigation-open")
      this.reset()
    }

    // SPは下へ下がるアニメーションの完了を待ってから閉じる
    if (this.isPc()) {
      finish()
      return
    }

    this.panelTarget.addEventListener("animationend", finish, { once: true })
    this.panelTarget.classList.add("is-closing")
    this.overlayTarget.classList.add("is-closing")
  }

  // フッターが起動ボタンに重なる間はボタンを隠す（フッター内のボタン類との重なりを防ぐ）
  // 判定範囲を画面下端からボタン下端までの距離だけ狭め、フッター上端がボタン下端より上に来たら隠す
  observeFooter() {
    if (!this._footer) return

    const launcherBottom = parseFloat(getComputedStyle(this.launcherTarget).bottom) || 0
    if (this._footerObserver && this._observedLauncherBottom === launcherBottom) return

    this._footerObserver?.disconnect()
    this._observedLauncherBottom = launcherBottom
    this._footerObserver = new IntersectionObserver(([entry]) => {
      this.launcherTarget.classList.toggle("is-footer-visible", entry.isIntersecting)
    }, { rootMargin: `0px 0px -${launcherBottom}px 0px` })
    this._footerObserver.observe(this._footer)
  }

  isPc() {
    return window.matchMedia("(width >= 992px)").matches
  }

  positionLauncher() {
    if (this._main && this.isPc()) {
      this.setLauncherRight()
    } else {
      this.element.style.removeProperty("--qa-navigation-launcher-right")
    }

    // PCは位置が確定するまで非表示にしている（表示後に位置が動くのを防ぐ）
    this.launcherTarget.classList.add("is-positioned")
  }

  setLauncherRight() {
    // fixed の right はスクロールバーを除いた表示幅が基準のため clientWidth で計算する
    const viewportWidth = document.documentElement.clientWidth
    const mainRight = this._main.getBoundingClientRect().right
    const right = viewportWidth - mainRight - LAUNCHER_GAP - LAUNCHER_SIZE

    // 画面が狭くボタンがはみ出す場合は右端から最小余白で止める
    this.element.style.setProperty("--qa-navigation-launcher-right", `${Math.max(right, LAUNCHER_MIN_RIGHT)}px`)
  }

  showMenu() {
    this.showScreen("menu")
    this.fitQuestionList()
  }

  // 5件目の中央までが見える高さに一覧を制限する（質問文の行数で高さが変わるため実測する）
  fitQuestionList() {
    if (!this.hasQuestionListTarget) return

    const list = this.questionListTarget
    const items = list.children

    list.style.removeProperty("max-height")
    if (items.length <= VISIBLE_QUESTION_COUNT) return

    list.scrollTop = 0
    const listTop = list.getBoundingClientRect().top
    const peekItem = items[VISIBLE_QUESTION_COUNT].getBoundingClientRect()
    const maxHeight = peekItem.top - listTop + peekItem.height / 2
    list.style.setProperty("max-height", `${maxHeight}px`)
  }

  // ホイール位置の要素がスクロールできればそれを、できなければ表示中画面の一覧・本文をスクロールする
  scrollPanel(event) {
    event.preventDefault()

    const deltaY = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY
    const scroller = this.findScroller(event.target, deltaY)
    if (scroller) scroller.scrollTop += deltaY
  }

  findScroller(target, deltaY) {
    const canScroll = (element) => {
      if (!element || element.scrollHeight <= element.clientHeight) return false
      // 吹き出しのしっぽ等で中身がはみ出しているだけの要素(overflow: visible)はスクロールできないため除外する
      if (!["auto", "scroll"].includes(getComputedStyle(element).overflowY)) return false
      if (deltaY > 0) return element.scrollTop + element.clientHeight < element.scrollHeight - 1
      return element.scrollTop > 0
    }

    const screen = this.screenTargets.find((element) => !element.hidden)
    if (!screen) return null

    const candidates = []
    for (let element = target; element && element !== screen; element = element.parentElement) {
      candidates.push(element)
    }
    candidates.push(
      screen.querySelector(".qa-navigation__question-list"),
      screen.querySelector(".qa-navigation__body"),
    )

    return candidates.find(canScroll) || null
  }

  showAnswer(event) {
    const index = event.params.index
    const question = event.currentTarget.textContent.trim()

    if (this.hasAnswerQuestionTarget) {
      this.answerQuestionTarget.textContent = question
    }

    this.answerBlockTargets.forEach((block) => {
      block.hidden = block.dataset.qaNavigationAnswerIndex !== String(index)
    })

    this.showScreen("answer")
  }

  showFormInput() {
    this.showScreen("form-input")
  }

  showFormConfirm() {
    const body = this.bodyInputTarget.value.trim()

    if (body === "") {
      this.showError("お問い合わせ内容を入力してください。")
      this.bodyInputTarget.focus()
      return
    }

    this.hideError()
    this.confirmBodyTarget.textContent = body
    this.showScreen("form-confirm")
  }

  submit() {
    const body = this.bodyInputTarget.value.trim()
    if (body === "") return

    this.submitButtonTarget.disabled = true

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch("/mypage/inquiries", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ member_inquiry: { body: body } }),
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to submit inquiry")
        this.showScreen("form-complete")
      })
      .catch((error) => {
        console.error(error)
        this.submitButtonTarget.disabled = false
        this.showFormInput()
        this.showError("送信に失敗しました。時間をおいて再度お試しください。")
      })
  }

  reset() {
    this.bodyInputTarget.value = ""
    this.confirmBodyTarget.textContent = ""
    this.submitButtonTarget.disabled = false
    this.hideError()
  }

  showScreen(name) {
    this.screenTargets.forEach((screen) => {
      screen.hidden = screen.dataset.qaNavigationScreen !== name
    })
    this.panelTarget.scrollTop = 0
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }
}
